import {
  useQuery,
  UseQueryOptions,
  UseQueryResult,
} from '@tanstack/react-query';
import type { Session as NextAuthSession } from 'next-auth';

import { mountAuthHeaders } from '~/shared/auth/mountAuthHeaders';
import { TICKET_CONSTANTS } from '~/shared/constants';
import { complianceApi } from '~/shared/services/api';
import { clearParams } from '~/shared/utils/clearParams';

import { FilteredTicketsResponse, TicketFilters } from '../types';

const { QUERY } = TICKET_CONSTANTS;

export const GET_TICKETS_BY_FILTER_QUERY_KEY = 'useGetTicketsByFilter';

export async function getTicketsByFilter(
  filters: TicketFilters,
  session?: NextAuthSession | null
) {
  const clearedParams = clearParams(filters);

  if (process.env.NODE_ENV === 'development') {
    console.log('Filter params being sent to API:', clearedParams);
  }

  const response = await complianceApi.get<FilteredTicketsResponse>(
    '/ticket/filter',
    {
      params: clearedParams,
      headers: mountAuthHeaders(session),
      timeout: QUERY.REQUEST_TIMEOUT,
    }
  );

  if (process.env.NODE_ENV === 'development') {
    console.log('Filter API response:', response.data);
  }

  return response.data;
}

export function useGetTicketsByFilter<T = FilteredTicketsResponse>(
  filters: TicketFilters,
  queryOptions?: Omit<
    UseQueryOptions<
      FilteredTicketsResponse,
      unknown,
      T,
      [string, TicketFilters]
    >,
    'queryKey' | 'queryFn'
  >
): UseQueryResult<T> {
  return useQuery<FilteredTicketsResponse, unknown, T, [string, TicketFilters]>(
    {
      queryKey: [GET_TICKETS_BY_FILTER_QUERY_KEY, filters],
      queryFn: () => getTicketsByFilter(filters),
      enabled: true,
      retry: (failureCount: number, error: any) => {
        if (error?.response?.status === 401) return false;
        if (error?.response?.status === 400) {
          console.error('Invalid filter parameters:', filters);
          return false;
        }
        return failureCount < QUERY.MAX_RETRIES;
      },
      staleTime: QUERY.STALE_TIME,
      ...queryOptions,
    }
  );
}
